import axios, { AxiosResponse } from "axios";
import MapView from "@arcgis/core/views/MapView";
import {
  ELocationServicesErrorMessages,
  EMarkerStatuses,
  LOCATOR_URL,
} from "./Map.constants";
import Extent from "@arcgis/core/geometry/Extent";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Popup from "@arcgis/core/widgets/Popup";
import Geometry from "@arcgis/core/geometry/Geometry";

import {
  ILocationServicesError,
  IMapPoint,
  IMapPointInfo,
  MapViewClickEvent,
} from "./Map.types";
import { ApiService } from "../../api/api.service";
import { OutagesResponse, OutageViewModel } from "../../api/generated/models";
import { ELogTypes } from "../../shared/enums/logging.enums";
import {
  getOutagesSuccess,
  getOutagesFailed,
} from "../../state-management/slices/outages/outages.slice";
import { DispatchLogger } from "../../state-management/utils/dispatch-logger/DispatchLogger";
import { FeatureLayerService } from "./FeatureLayer.service";
import * as projection from "@arcgis/core/geometry/projection";
import {
  getGlobalState,
  getOutagesState,
} from "../../shared/utils/testing.utils";
import { updateMarkerGeoLoc } from "../../state-management/slices/global/global.slice";
import { renderPopup } from "./widgets/Popup/Popup.render";
import { AppDispatch } from "../../state-management/store";
import {
  EBracketTypes,
  LampTypeDisplayMap,
} from "../../shared/enums/utility.enums";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";

import SelectedStreetlightMarker from "../../theme/assets/map-markers/dot_streetlight-selected.svg";
import SelectedExtendedRepairMarker from "../../theme/assets/map-markers/dot_extended-repair-selected.svg";
import SelectedReportedMarker from "../../theme/assets/map-markers/dot_reported-selected.svg";
import SelectedNonCEMarker from "../../theme/assets/map-markers/dot_non-ce-selected.svg";

import StreetlightMarker from "../../theme/assets/map-markers/dot_streetlight.svg";
import ExtendedRepairMarker from "../../theme/assets/map-markers/dot_extended-repair.svg";
import ReportedMarker from "../../theme/assets/map-markers/dot_reported.svg";
import NonCEMarker from "../../theme/assets/map-markers/dot_non-ce.svg";
import { isNullOrUndefined } from "../../shared/utils/data.utils";

/**
 * getLocationError
 * @param {GeolocationPositionError} error
 * @returns {ILocationServicesError}
 */
const getLocationError = (
  error: GeolocationPositionError
): ILocationServicesError => {
  let locationError: ILocationServicesError;
  const { code, PERMISSION_DENIED, POSITION_UNAVAILABLE, TIMEOUT } = error;
  switch (code) {
    case PERMISSION_DENIED:
      locationError = {
        code: code,
        message: ELocationServicesErrorMessages.PERMISSION_DENIED,
        displayMessage: ELocationServicesErrorMessages.PERMISSION_DENIED,
      };
      break;
    case POSITION_UNAVAILABLE:
      locationError = {
        code: code,
        message: ELocationServicesErrorMessages.POSITION_UNAVAILABLE,
        displayMessage: ELocationServicesErrorMessages.PERMISSION_DENIED,
      };
      break;
    case TIMEOUT:
      locationError = {
        code: code,
        message: ELocationServicesErrorMessages.TIMEOUT,
        displayMessage: ELocationServicesErrorMessages.PERMISSION_DENIED,
      };
      break;
    default:
      locationError = {
        code: code,
        message: ELocationServicesErrorMessages.UNKNOWN,
        displayMessage: ELocationServicesErrorMessages.PERMISSION_DENIED,
      };
      break;
  }

  return locationError;
};

/**
 * getPointLatLong
 * @param {MapViewClickEvent} event
 * @return {*}  {IMapPoint}
 * @memberof MapUtils
 */
const getPointLatLong = (event: MapViewClickEvent): IMapPointInfo => {
  const { latitude, longitude, x, y } = event.mapPoint;
  return { latitude, longitude, x, y };
};

const fetchCrossStreets = async (geometry: Geometry): Promise<string> => {
  const xStreetResponse = await axios
    .get(`${LOCATOR_URL}/reverseGeocode`, {
      params: {
        location: geometry,
        featureTypes: "StreetInt",
        f: "json",
      },
    })
    .then((response) => response)
    .catch((errors) => {
      return errors;
    });

  if (xStreetResponse.data.error || !xStreetResponse.data.address) {
    return "Address Unknown";
  }
  return xStreetResponse;
};

const fetchGeoLocData = async (mapPoint: IMapPoint): Promise<unknown> => {
  let geoLocResponse = null;
  try {
    geoLocResponse = await axios
      .all([
        axios.get(`${LOCATOR_URL}/reverseGeocode`, {
          params: {
            location: mapPoint,
            featureTypes: "StreetName",
            f: "json",
          },
        }),
        axios.get(`${LOCATOR_URL}/reverseGeocode`, {
          params: {
            location: mapPoint,
            featureTypes: "StreetAddress",
            f: "json",
          },
        }),
        axios.get(`${LOCATOR_URL}/reverseGeocode`, {
          params: {
            location: mapPoint,
            featureTypes: "PointAddress",
            f: "json",
          },
        }),
      ])
      .then(
        axios.spread(
          (streetNameResponse, streetAddressResponse, pointAddressResponse) => {
            return {
              streetNameResponse,
              streetAddressResponse,
              pointAddressResponse,
            };
          }
        )
      )
      .catch((errors) => {
        return errors;
      });
  } catch (error) {
    return error;
  }

  return geoLocResponse;
};

/**
 * buildStreetAddressString
 * @param {string} address
 * @param {string} city
 * @param {string} region
 * @return {*}  {string}
 */
const buildStreetAddressString = (addressResponse: any): string => {
  const { Address, City, Region } = addressResponse.data.address;
  let streetAddress;

  if (Address && City && Region) {
    streetAddress = `${Address}, ${City} ${Region}`;
  } else if (Address && City) {
    streetAddress = `${Address}, ${City}`;
  } else {
    streetAddress = `${Address}`;
  }

  return streetAddress;
};

/**
 * buildGeoLocPayload
 * @param {*} geoLocResponse
 * @param {*} xStreetsResponse
 * @return {*}  {{ streetAddress: string; geoLocPayload: any }}
 */
const buildGeoLocPayload = (
  geoLocResponse: any,
  xStreetsResponse: any
): { streetAddress: string; geoLocPayload: any } => {
  const { streetNameResponse, streetAddressResponse, pointAddressResponse } =
    geoLocResponse;
  let geoLocPayload = null;
  let streetAddress = null;
  xStreetsResponse = isNullOrUndefined(xStreetsResponse.data)
    ? streetAddressResponse
    : xStreetsResponse;
  if (streetAddressResponse.data.error && pointAddressResponse.data.error) {
    streetAddress = buildStreetAddressString(xStreetsResponse);
    geoLocPayload = {
      ...xStreetsResponse.data,
      crossStreets: xStreetsResponse.data.address.Address,
    };
  } else {
    // IF Point Address has an error, fall back to street address
    if (pointAddressResponse.data.error) {
      streetAddress = buildStreetAddressString(streetAddressResponse);
      geoLocPayload = {
        ...streetAddressResponse.data,
        crossStreets: xStreetsResponse.data.address.Address,
      };
    } else {
      // if pointAddress IS ON the same street as streetAddress,
      // we want to use pointAddress. If point address does not exist,
      // fall back to streetAddress. Else, if pointAddress is NOT on the
      // same street as streetAddress, then fall back to streetAddress.
      if (streetAddressResponse.data.error) {
        streetAddress = buildStreetAddressString(pointAddressResponse);
        geoLocPayload = {
          ...pointAddressResponse.data,
          crossStreets: xStreetsResponse.data.address.Address,
        };
      } else {
        if (
          pointAddressResponse.data.address.Address.includes(
            streetNameResponse.data.address.Address
          )
        ) {
          streetAddress = buildStreetAddressString(pointAddressResponse);
          geoLocPayload = {
            ...pointAddressResponse.data,
            crossStreets: xStreetsResponse.data.address.Address,
          };
        } else {
          // if pointAddress is NOT on the
          // same street as streetAddress, then fall back to streetAddress.
          streetAddress = buildStreetAddressString(streetAddressResponse);
          geoLocPayload = {
            ...streetAddressResponse.data,
            crossStreets: xStreetsResponse.data.address.Address,
          };
        }
      }
    }
  }

  return { streetAddress, geoLocPayload };
};

/**
 * projectLatLngFromExtent
 * @param {Extent} extent
 * @return {*}
 */
const projectLatLngFromExtent = (extent: Extent): any => {
  return projection.project(
    extent,
    new SpatialReference({
      wkid: 4326,
    })
  );
};

/**
 * processExtent
 * @param {Extent} currentExtent
 * @param {ApiService} apiService
 * @param {DispatchLogger} dispatchLogger
 * @param {FeatureLayerService} featureLayerService
 * @return {*}  {Promise<void>}
 */
const processExtent = async (
  currentExtent: Extent,
  apiService: ApiService,
  dispatchLogger: DispatchLogger,
  featureLayerService: FeatureLayerService
): Promise<unknown> => {
  try {
    const outages = (await apiService.outagesApi.getOutagesByExtent({
      currentExtent,
    })) as OutagesResponse;

    dispatchLogger.fire(
      getOutagesSuccess,
      { ...outages },
      {
        message: "Outages Fetched",
        type: ELogTypes.API,
        data: { outages },
      }
    );

    featureLayerService.fetchFeatureLayer(currentExtent, outages);
  } catch (error) {
    dispatchLogger.fire(
      getOutagesFailed,
      { error },
      {
        message: "Outages Fetched",
        type: ELogTypes.ERROR,
        data: { error },
      }
    );

    return error;
  }
};

/**
 * Note: this is a workaround to get the the locate icon to render properly.
 * This solution is backwards compatible, If and whenever ESRI decides to fix it.
 * @see https://stackoverflow.com/questions/65563851/arcgis-4-18-locate-widget-custom-icon
 */
const renderLocationButtonIcon = (): void => {
  const locateIcons = document.getElementsByClassName("esri-icon-locate");
  if (locateIcons && locateIcons.length === 1) {
    locateIcons[0].classList.value += " esri-icon-navigation";
  }
};

const getPinModePopupContent = (
  mapView: MapView,
  streetAddress: string,
  removePinModeGraphics: () => void
): any => {
  return renderPopup({
    address: streetAddress,
    markerAttributes: {
      lampType: EBracketTypes.UNKNOWN,
      markerStatus: EMarkerStatuses.COMPLETED,
      bracketType: EBracketTypes.UNKNOWN,
      GISID: null,
    },
    reportedDate: null,
    sapNotificationId: null,
    onClose: () => {
      removePinModeGraphics();
      mapView.popup.close();
    },
  });
};

const getRoutingPopupContent = (
  mapView: MapView,
  streetAddress: string,
  outage: OutageViewModel,
  removeMapViewGraphics: () => void
): any => {
  const { utility, location, markerStatus } = outage;
  const reportedDateString = new Date(outage.reportedDateTime as string);
  const reportedDate = new Intl.DateTimeFormat("en-US", {
    timeStyle: "short",
    dateStyle: "long",
  } as any).format(reportedDateString);

  return renderPopup({
    address: streetAddress,
    markerAttributes: {
      lampType: utility?.lampType as string,
      markerStatus: markerStatus,
      bracketType: utility?.bracketType as any,
      GISID: location?.locationId,
    },
    reportedDate,
    sapNotificationId: outage.sapNotification as string,
    onClose: () => {
      removeMapViewGraphics();
      mapView.popup.close();
    },
  });
};

const getGrandRapidsPopupContent = (
  dispatch: AppDispatch,
  mapView: MapView,
  popup: Popup,
  featureLayerID: string
): any => {
  return async (feature: { graphic: any }): Promise<unknown> => {
    const { graphic } = feature;
    const { geometry, layer } = graphic;

    const geoLocResponse = await fetchGeoLocData(geometry);
    const xStreetsResponse = await fetchCrossStreets(geometry);
    const { streetAddress, geoLocPayload } = buildGeoLocPayload(
      geoLocResponse,
      xStreetsResponse
    );

    // Update state
    dispatch(updateMarkerGeoLoc(geoLocPayload));

    return renderPopup({
      address: streetAddress,
      markerAttributes: {
        lampType: LampTypeDisplayMap.UNK,
        markerStatus: EMarkerStatuses.NON_CE,
        bracketType: EBracketTypes.COBRA,
        GISID: null,
      },
      reportedDate: null,
      sapNotificationId: null,
      layerId: layer.id,
      onClose: () => {
        mapView.graphics.removeAll();
        popup.close();
      },
    });
  };
};

const getPopupContent = (
  dispatch: AppDispatch,
  mapView: MapView,
  popup: Popup,
  featureLayerID: string
) => {
  return async (feature: { graphic: any }): Promise<unknown> => {
    const { graphic } = feature;
    const { ui } = getGlobalState();

    if (ui.pinMode.enabled && graphic.sourceLayer.id === featureLayerID) {
      return;
    }

    const { geometry } = graphic;
    const ESRI_ATTRIBUTES = graphic.attributes;

    let reportedDate = null;
    let sapNotificationId = null;
    let attributes: {
      markerStatus?: any;
      GISID?: any;
      LAMPTYPE?: any;
      FIXTURESUP?: any;
      LATITUDE?: any;
      LONGITUDE?: any;
    } | null = null;

    // IF we dont have a GISID, we can safely assume
    // we have a dropped pin. IF so, we need the attributes
    // from the selectedMarker, OTHERWISE, we can use the
    // standard ESRI Attributes.

    if (!ESRI_ATTRIBUTES.GISID) {
      const { selectedMarker } = ui;
      const { streetLightData } = selectedMarker;

      if (streetLightData) {
        attributes = {
          ...ESRI_ATTRIBUTES,
          ...streetLightData.attributes,
        };
      }
    } else {
      attributes = {
        ...ESRI_ATTRIBUTES,
      };
    }

    const geoLocResponse = await fetchGeoLocData(geometry);
    const xStreetsResponse = await fetchCrossStreets(geometry);
    const { streetAddress, geoLocPayload } = buildGeoLocPayload(
      geoLocResponse,
      xStreetsResponse
    );

    // Update state
    dispatch(updateMarkerGeoLoc(geoLocPayload));

    if (attributes && attributes.markerStatus !== EMarkerStatuses.COMPLETED) {
      const outagesState = getOutagesState();
      const { outageCollection } = outagesState;

      Object.values(outageCollection).forEach((outage: OutageViewModel) => {
        if (
          (outage.location?.locationId &&
            outage.location?.locationId === attributes?.GISID) ||
          (outage.location?.coordinates?.latitude === attributes?.LATITUDE &&
            outage.location?.coordinates?.longitude === attributes?.LONGITUDE)
        ) {
          const reportedDateString = new Date(
            outage.reportedDateTime as string
          );

          sapNotificationId = outage.sapNotification;
          reportedDate = new Intl.DateTimeFormat("en-US", {
            timeStyle: "short",
            dateStyle: "long",
          } as any).format(reportedDateString);
        }
      });
    }

    return renderPopup({
      address: streetAddress,
      markerAttributes: {
        lampType: attributes?.LAMPTYPE,
        markerStatus: attributes?.markerStatus,
        bracketType: attributes?.FIXTURESUP,
        GISID: attributes?.GISID,
      },
      reportedDate,
      sapNotificationId,
      onClose: () => {
        mapView.graphics.removeAll();
        popup.close();
      },
    });
  };
};

/**
 * getMarkerByStatus
 * @param {string} markerStatus
 * @param {boolean} selected
 * @return {*}  {PictureMarkerSymbol}
 */
const getMarkerByStatus = (
  markerStatus: string,
  selected = false
): PictureMarkerSymbol => {
  let symbol = null;
  switch (markerStatus) {
    case EMarkerStatuses.REPORTED:
      symbol = new PictureMarkerSymbol({
        url: selected ? SelectedReportedMarker : ReportedMarker,
        width: 12,
        height: 12,
      });
      break;
    case EMarkerStatuses.IN_PROGRESS:
      symbol = new PictureMarkerSymbol({
        url: selected ? SelectedReportedMarker : ReportedMarker,
        width: 12,
        height: 12,
      });
      break;
    case EMarkerStatuses.EXTENDED_REPAIR:
      symbol = new PictureMarkerSymbol({
        url: selected ? SelectedExtendedRepairMarker : ExtendedRepairMarker,
        width: 12,
        height: 12,
      });
      break;
    case EMarkerStatuses.NON_CE:
      symbol = new PictureMarkerSymbol({
        url: selected ? SelectedNonCEMarker : NonCEMarker,
        width: 12,
        height: 12,
      });
      break;

    default:
      symbol = new PictureMarkerSymbol({
        url: selected ? SelectedStreetlightMarker : StreetlightMarker,
        width: 12,
        height: 12,
      });
      break;
  }

  return symbol;
};

export default {
  buildGeoLocPayload,
  fetchGeoLocData,
  fetchCrossStreets,
  getLocationError,
  getPointLatLong,
  getPinModePopupContent,
  getGrandRapidsPopupContent,
  getPopupContent,
  getRoutingPopupContent,
  getMarkerByStatus,
  processExtent,
  projectLatLngFromExtent,
  renderLocationButtonIcon,
};
